import { FC, useEffect, useState } from 'react';
import { SimpleGrid, Skeleton, useToast } from '@chakra-ui/react';
import { useProgramsRewards, useProgramsStaked } from 'hooks/query/usePrograms';
import { fetchBlockTimestamp } from 'api/blockchain/blockTimestamp';
import { useWeb3React } from 'hooks/web3';
import { useParams } from 'react-router';
import { ExtendedRewardProgram } from 'types';
import RewardProgramCard from './RewardProgramCard';

type Props = {
  programs: ExtendedRewardProgram[] | undefined;
  showExpiredPrograms: boolean;
  showUnverified: boolean;
};

const RewardProgramsList: FC<Props> = ({
  programs,
  showExpiredPrograms,
  showUnverified
}) => {
  const { chainId = 1 } = useWeb3React();
  const [blockTimestamp, setBlockTimestamp] = useState<number>();
  const { rewardProgramAddress } =
    useParams<{ rewardProgramAddress: string }>();
  const toast = useToast();

  useEffect(() => {
    fetchBlockTimestamp(chainId).then((ts) => {
      setBlockTimestamp(ts);
    });
  }, [chainId]);

  const { data: totalRewardsData } = useProgramsRewards();
  const { data: totalStakedData } = useProgramsStaked();

  useEffect(() => {
    if (
      rewardProgramAddress &&
      programs &&
      !programs.find(
        (program) =>
          program.rewardProgramAddress.toLowerCase() ===
          rewardProgramAddress.toLowerCase()
      ) &&
      !toast.isActive('rewardProgramNotFound')
    ) {
      toast({
        id: 'rewardProgramNotFound',
        title: 'The reward program could not be found',
        status: 'warning',
        duration: 4000,
        isClosable: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardProgramAddress, programs]);

  const skeletonProps = {
    height: '283px',
    maxHeight: '283px',
    startColor: 'gray.700',
    endColor: 'gray.600',
    borderRadius: 'xl'
  };

  if (!programs || !totalRewardsData || !totalStakedData || !blockTimestamp) {
    return (
      <SimpleGrid columns={[1, null, 2, null, 3]} spacing={[5, null, null, 10]}>
        <Skeleton {...skeletonProps} />
        <Skeleton {...skeletonProps} />
        <Skeleton {...skeletonProps} />
        <Skeleton {...skeletonProps} />
        <Skeleton {...skeletonProps} />
        <Skeleton {...skeletonProps} />
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid columns={[1, null, 2, null, 3]} spacing={[5, null, null, 10]}>
      {[...programs]
        .sort(function (a, b) {
          return !a.rewardProgram.expiryTimestamp
            ? -1
            : a.rewardProgram.expiryTimestamp > blockTimestamp
            ? -1
            : 1;
        })
        .sort(
          (a, b) =>
            Number(b.rewardProgram.featured) - Number(a.rewardProgram.featured)
        )
        .map((extendedRewardProgram) => (
          <RewardProgramCard
            key={extendedRewardProgram.rewardProgram.address}
            extendedRewardProgram={extendedRewardProgram}
            programRewardsUSD={
              totalRewardsData[extendedRewardProgram.rewardProgram.address]
            }
            programStakedUSD={
              totalStakedData[extendedRewardProgram.rewardProgram.address]
            }
            blockTimestamp={blockTimestamp}
            showExpiredPrograms={showExpiredPrograms}
            showUnverified={showUnverified}
          />
        ))}
    </SimpleGrid>
  );
};

export default RewardProgramsList;
